import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const COURSE_URL = "https://fetc.in/api/v1/courses";

export const courseApi = createApi({
  reducerPath: "coursesApi",
  tagTypes: ["Courses", "Progress"],

  baseQuery: fetchBaseQuery({
    baseUrl: COURSE_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().auth?.token;
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),

  refetchOnFocus: true,
  refetchOnReconnect: true,

  endpoints: (builder) => ({
    /* ======================================================
       📚 COURSES
    ====================================================== */

    createCourse: builder.mutation({
      query: (formData) => ({
        url: "/create",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: [{ type: "Courses", id: "LIST" }],
    }),

    getCourses: builder.query({
      query: (params) => ({
        url: "/list",
        params,
      }),
      providesTags: (result) =>
        result?.courses
          ? [
              ...result.courses.map((course) => ({
                type: "Courses",
                id: course.slug,
              })),
              { type: "Courses", id: "LIST" },
            ]
          : [{ type: "Courses", id: "LIST" }],
    }),

    getPublishedCourses: builder.query({
      query: () => "/published",
      providesTags: [{ type: "Courses", id: "LIST" }],
    }),

    getCreatorCourses: builder.query({
      query: () => "/user",
      providesTags: [{ type: "Courses", id: "LIST" }],
    }),

    /* 🔥 Fetch by slug (not ID) */
    getCourseBySlug: builder.query({
      query: (slug) => `/${slug}`,
      providesTags: (result, error, slug) => [
        { type: "Courses", id: slug },
      ],
    }),

    editCourse: builder.mutation({
      query: ({ slug, data }) => ({
        url: `/${slug}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { slug }) => [
        { type: "Courses", id: slug },
        { type: "Courses", id: "LIST" },
      ],
    }),

    deleteCourse: builder.mutation({
      query: (slug) => ({
        url: `/${slug}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Courses", id: "LIST" }],
    }),

    /* ======================================================
       📘 LESSON MANAGEMENT
    ====================================================== */

    addLesson: builder.mutation({
      query: ({ courseSlug, lessonData }) => ({
        url: `/${courseSlug}/lessons`,
        method: "POST",
        body: lessonData,
      }),
      invalidatesTags: (result, error, { courseSlug }) => [
        { type: "Courses", id: courseSlug },
      ],
    }),

    editLesson: builder.mutation({
      query: ({ courseSlug, lessonId, lessonData }) => ({
        url: `/${courseSlug}/lessons/${lessonId}`,
        method: "PUT",
        body: lessonData,
      }),
      invalidatesTags: (result, error, { courseSlug }) => [
        { type: "Courses", id: courseSlug },
      ],
    }),

    deleteLesson: builder.mutation({
      query: ({ courseSlug, lessonId }) => ({
        url: `/${courseSlug}/lessons/${lessonId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { courseSlug }) => [
        { type: "Courses", id: courseSlug },
      ],
    }),

    /* ======================================================
       📊 COURSE PROGRESS
    ====================================================== */

    getCourseProgress: builder.query({
      query: (courseId) => `/${courseId}/progress`,
      providesTags: (result, error, courseId) => [
        { type: "Progress", id: courseId },
      ],
      transformResponse: (response) => ({
        lessonsProgress: response.lessonsProgress,
        percentage: response.percentage,
        score: response.score,
        isCompleted: response.isCompleted,
      }),
    }),

    updateLessonCompletion: builder.mutation({
      query: ({ courseId, lessonId, completed, score }) => ({
        url: `/${courseId}/lessons/${lessonId}/progress`,
        method: "POST",
        body: { completed, score },
      }),
      invalidatesTags: (result, error, { courseId }) => [
        { type: "Progress", id: courseId },
      ],
    }),

    saveCourseResult: builder.mutation({
      query: ({ courseId, lessonId, score, completed }) => ({
        url: `/${courseId}/submit`,
        method: "POST",
        body: { lessonId, score, completed },
      }),
      invalidatesTags: (result, error, { courseId }) => [
        { type: "Progress", id: courseId },
      ],
    }),

    getCourseResult: builder.query({
      query: (courseId) => `/${courseId}/result`,
      providesTags: (result, error, courseId) => [
        { type: "Progress", id: courseId },
      ],
    }),

    /* ======================================================
       🎓 STUDENT EXERCISES
    ====================================================== */

    startLessonExercise: builder.mutation({
      query: ({ lessonId }) => ({
        url: `/student/lessons/${lessonId}/start`,
        method: "POST",
      }),
    }),

    submitLessonExercise: builder.mutation({
      query: ({ attemptId, answers }) => ({
        url: `/student/attempts/${attemptId}/submit`,
        method: "POST",
        body: { answers },
      }),
    }),

    getLessonResult: builder.query({
      query: (attemptId) =>
        `/student/attempts/${attemptId}/result`,
    }),

    /* ======================================================
       📤 FILE UPLOADS
    ====================================================== */

    uploadCourseFile: builder.mutation({
      query: (formData) => ({
        url: "/upload/file",
        method: "POST",
        body: formData,
      }),
    }),

    uploadCourseImage: builder.mutation({
      query: (formData) => ({
        url: "/upload/image",
        method: "POST",
        body: formData,
      }),
    }),

    uploadCourseVideo: builder.mutation({
      query: (formData) => ({
        url: "/upload/video",
        method: "POST",
        body: formData,
      }),
    }),

    uploadCourseAudio: builder.mutation({
      query: (formData) => ({
        url: "/upload/audio",
        method: "POST",
        body: formData,
      }),
    }),
  }),
});

/* ======================================================
   🔄 EXPORT HOOKS
====================================================== */

export const {
  useCreateCourseMutation,
  useGetCoursesQuery,
  useGetPublishedCoursesQuery,
  useGetCreatorCoursesQuery,
  useGetCourseBySlugQuery,
  useEditCourseMutation,
  useDeleteCourseMutation,
  useAddLessonMutation,
  useEditLessonMutation,
  useDeleteLessonMutation,
  useGetCourseProgressQuery,
  useUpdateLessonCompletionMutation,
  useSaveCourseResultMutation,
  useGetCourseResultQuery,
  useStartLessonExerciseMutation,
  useSubmitLessonExerciseMutation,
  useGetLessonResultQuery,
  useUploadCourseFileMutation,
  useUploadCourseImageMutation,
  useUploadCourseVideoMutation,
  useUploadCourseAudioMutation,
} = courseApi;